"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.AI_CITABLE_RULES = exports.RULE_ID_TO_RULE = exports.reportReasonSchema = exports.aiCitableRuleSchema = exports.moderationRuleSchema = exports.RULE_ID_TO_TEXT = exports.CIVILITY_RULE_ID = void 0;
exports.isAiCitableRule = isAiCitableRule;
exports.ruleIdsToModerationRules = ruleIdsToModerationRules;
exports.moderationRulesToRuleIds = moderationRulesToRuleIds;
exports.textsToModerationRules = textsToModerationRules;
exports.moderationRulesToTexts = moderationRulesToTexts;
exports.reportReasonsToTexts = reportReasonsToTexts;
/**
 * Canonical map from the numeric moderation rule IDs emitted by
 * `server/prompts/moderate.txt` to the exact English guideline text a
 * comment/mod-task row has always stored.
 *
 * These strings exist only to write and match that stored text — they are
 * not the copy shown to anyone. The translatable display labels live
 * separately as `t(...)` calls in the `GUIDELINES` catalog
 * (`./community-guideline-labels.ts`), hand-kept in sync with these, not
 * derived from them. IDs 1 and 11 intentionally map to the same civility
 * text, and unknown IDs fall back to it (`CIVILITY_RULE_ID`) — keep that
 * behavior or moderation breaks on prompt drift.
 */
const z = __importStar(require("zod"));
exports.CIVILITY_RULE_ID = 1;
exports.RULE_ID_TO_TEXT = {
    1: 'Express your ideas with civility',
    2: 'No violent content or calls for harm',
    3: 'Focus on ideas, rather than character judgments',
    4: 'Avoid derogatory labels',
    5: 'Avoid sarcasm and dismissive responses',
    6: 'Explain disagreements constructively',
    7: 'Provide sources for factual claims',
    8: 'No spam or commercial promotion',
    9: 'Respect the privacy of others, no personal information sharing',
    10: 'No off-topic posts',
    11: 'Express your ideas with civility',
};
exports.moderationRuleSchema = z.enum([
    'NO_UNCIVIL',
    'NO_HARM',
    'NO_INSULTS',
    'NO_LABELS',
    'BACKUP_CLAIMS',
    'NO_SPAM',
    'RESPECT_PRIVACY',
    'OFF_TOPIC',
    'NO_ILLEGAL',
    'NO_PERSONAL_ATTACKS',
    'BE_CONSTRUCTIVE',
]);
/**
 * The rules an AI citation or a human annotation of one may name. Excludes
 * `NO_ILLEGAL`: no prompt cites it, and the numeric-id storage these lists
 * still use has no id to store it under, so accepting it would drop it
 * silently. Kept in step with `AI_CITABLE_RULES` by a test.
 */
exports.aiCitableRuleSchema = exports.moderationRuleSchema.exclude(['NO_ILLEGAL']);
/**
 * What a reporter may name: any guideline, plus `OTHER` for an objection that
 * isn't one of them.
 *
 * A superset rather than a member of `ModerationRule`, because `OTHER` is not
 * a guideline — it has no label in the `GUIDELINES` catalog and no numeric
 * prompt ID, and it must not reach the rule-keyed surfaces (a comment's
 * `moderationState`, a mod task, a premod review) that only ever carry real
 * rules.
 */
exports.reportReasonSchema = z.enum([...exports.moderationRuleSchema.options, 'OTHER']);
/**
 * Maps the AI's numeric rule IDs (`server/prompts/moderate.txt` and
 * `moderatePerspective.txt` share the same 1-11 scheme) to the stable
 * `ModerationRule` key the `GUIDELINES` catalog looks its
 * translatable label up by. IDs 1 and 11 intentionally map to the same
 * `NO_UNCIVIL` rule, mirroring `RULE_ID_TO_TEXT` above.
 */
exports.RULE_ID_TO_RULE = {
    1: 'NO_UNCIVIL',
    2: 'NO_HARM',
    3: 'NO_PERSONAL_ATTACKS',
    4: 'NO_LABELS',
    5: 'NO_INSULTS',
    6: 'BE_CONSTRUCTIVE',
    7: 'BACKUP_CLAIMS',
    8: 'NO_SPAM',
    9: 'RESPECT_PRIVACY',
    10: 'OFF_TOPIC',
    11: 'NO_UNCIVIL',
};
/**
 * The rules a moderation prompt can actually cite. Derived from
 * `RULE_ID_TO_RULE` rather than listed, so it cannot drift from the prompt
 * contract: a rule is AI-citable exactly when some prompt rule ID maps to it.
 * `NO_ILLEGAL` is absent — it is human-only.
 */
exports.AI_CITABLE_RULES = new Set(Object.values(exports.RULE_ID_TO_RULE));
/** Narrows a rule to one a prompt can cite, for filtering the full catalog. */
function isAiCitableRule(rule) {
    return exports.aiCitableRuleSchema.safeParse(rule).success;
}
/**
 * Converts the numeric rule IDs the moderation prompts emit into stable
 * `ModerationRule` keys, dropping any ID outside the documented 1-11 range.
 * Total: never throws, never yields an undefined entry, and de-duplicates —
 * IDs 1 and 11 are the same rule, so a response citing both yields one key.
 */
function ruleIdsToModerationRules(ruleIds) {
    const rules = (ruleIds ?? []).flatMap((ruleId) => {
        const rule = exports.RULE_ID_TO_RULE[Number(ruleId)];
        return rule ? [rule] : [];
    });
    return [...new Set(rules)];
}
/**
 * Inverse of `ruleIdsToModerationRules`, for writing back to the numeric IDs
 * that legacy storage and the prompts still speak. `NO_UNCIVIL` collapses to
 * `CIVILITY_RULE_ID`, the canonical one of its two interchangeable IDs, and
 * `NO_ILLEGAL` is dropped: it is human-only, has no ID, and inventing one
 * would put a value the prompts don't define into stored data.
 */
function moderationRulesToRuleIds(rules) {
    return (rules ?? []).flatMap((rule) => {
        const ruleId = RULE_TO_RULE_ID[rule];
        return ruleId === undefined ? [] : [ruleId];
    });
}
const RULE_TO_RULE_ID = Object.entries(exports.RULE_ID_TO_RULE).reduce((acc, [ruleId, rule]) => {
    const id = Number(ruleId);
    // Lowest ID wins, so NO_UNCIVIL canonicalizes to 1 rather than 11.
    if (acc[rule] === undefined || id < acc[rule])
        acc[rule] = id;
    return acc;
}, {});
/**
 * Every `ModerationRule`'s stored English text — same purpose as
 * `RULE_ID_TO_TEXT` above (matching/reconstructing legacy stored text, not
 * display copy), just keyed by rule instead of numeric ID so `NO_ILLEGAL` —
 * human-only, no numeric ID — has a text too. Kept as a separate map (rather
 * than deriving `RULE_ID_TO_TEXT` from it) since `RULE_ID_TO_TEXT` deliberately
 * stays untouched; the strings duplicate its values by design — if this
 * file's own English wording ever changes, keep both maps in sync.
 */
const RULE_TO_TEXT = {
    NO_UNCIVIL: 'Express your ideas with civility',
    NO_HARM: 'No violent content or calls for harm',
    NO_PERSONAL_ATTACKS: 'Focus on ideas, rather than character judgments',
    NO_LABELS: 'Avoid derogatory labels',
    NO_INSULTS: 'Avoid sarcasm and dismissive responses',
    BE_CONSTRUCTIVE: 'Explain disagreements constructively',
    BACKUP_CLAIMS: 'Provide sources for factual claims',
    NO_SPAM: 'No spam or commercial promotion',
    RESPECT_PRIVACY: 'Respect the privacy of others, no personal information sharing',
    OFF_TOPIC: 'No off-topic posts',
    NO_ILLEGAL: 'No illegal content',
};
const TEXT_TO_RULE = Object.fromEntries(Object.entries(RULE_TO_TEXT).map(([rule, text]) => [text, rule]));
/**
 * Converts stored English-guideline-text violations to their stable
 * `ModerationRule` keys, dropping anything that doesn't match a known
 * guideline (an old-format row, a user report's free-text entry, a reworded
 * guideline). Total: never throws, never lets unrecognized text through.
 */
function textsToModerationRules(texts) {
    return (texts ?? []).flatMap((text) => {
        const rule = TEXT_TO_RULE[text];
        return rule ? [rule] : [];
    });
}
/**
 * Inverse of `textsToModerationRules`. Total: every `ModerationRule` has
 * text.
 */
function moderationRulesToTexts(rules) {
    return (rules ?? []).map((rule) => RULE_TO_TEXT[rule]);
}
/**
 * The text a report's reasons have always been stored under. `OTHER` has no
 * guideline text of its own, so it keeps the literal label the report modal's
 * checkbox wrote before reports moved to rule keys.
 */
const OTHER_REASON_TEXT = 'Other';
/**
 * `moderationRulesToTexts` widened to the reasons a report can name, for
 * writing the stored form. Total: every `ReportReason` has text.
 */
function reportReasonsToTexts(reasons) {
    return (reasons ?? []).map((reason) => (reason === 'OTHER' ? OTHER_REASON_TEXT : RULE_TO_TEXT[reason]));
}
//# sourceMappingURL=community-guidelines.js.map